import type { WorkersResponse } from "@/lib/pb-types";

/**
 * Workers: the machines that pick Flows cards (docs/workers.md). Each reports
 * itself with a heartbeat at the start and end of a round; this module turns
 * those rows into what a node card says. Pure, so the page and the tests agree.
 */

/** The day's counters a machine keeps, reset on its first round of a Bangkok day. */
export type WorkerToday = {
  date?: string;
  rounds?: number;
  delivered?: number;
  needs_you?: number;
  released?: number;
  no_work?: number;
  failed?: number;
};

export type Worker = WorkersResponse<string[], WorkerToday>;

export type WorkerStatus = "idle" | "working" | "error" | "offline";

/** A machine that has not reported for this long is drawn as offline, whatever its last state. */
export const OFFLINE_AFTER_MS = 15 * 60_000;

/** The rate the server falls back to before its daily job has read one. */
export const FALLBACK_FX = 34;

export const STATUS_LABEL: Record<WorkerStatus, string> = {
  idle: "ว่าง",
  working: "กำลังทำ",
  error: "ติดปัญหา",
  offline: "ออฟไลน์",
};

/** PocketBase writes "2026-11-03 10:00:00.000Z"; an empty string is no date. */
export function parseStamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value.replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isOffline(lastSeen: string | null | undefined, now: Date): boolean {
  const seen = parseStamp(lastSeen);
  return !seen || now.getTime() - seen.getTime() > OFFLINE_AFTER_MS;
}

export function workerStatus(worker: Pick<Worker, "state" | "last_seen">, now: Date): WorkerStatus {
  if (isOffline(worker.last_seen, now)) return "offline";
  return worker.state === "working" || worker.state === "error" ? worker.state : "idle";
}

/** Monthly cost in baht, or null when the machine has no price yet. */
export function thbPerMonth(worker: Pick<Worker, "price_usd_month" | "fx_usd_thb">): number | null {
  const usd = worker.price_usd_month || 0;
  if (usd <= 0) return null;
  return usd * (worker.fx_usd_thb || FALLBACK_FX);
}

const THB = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const USD = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

/** "฿1,632" — whole baht. */
export function formatThb(value: number): string {
  return `฿${THB.format(Math.round(value))}`;
}

/** "$48", "$4.5". */
export function formatUsd(value: number): string {
  return `$${USD.format(value)}`;
}

/** "฿1,632/เดือน · $48", or "" when there is no price yet. */
export function priceLine(worker: Pick<Worker, "price_usd_month" | "fx_usd_thb">): string {
  const thb = thbPerMonth(worker);
  return thb === null ? "" : `${formatThb(thb)}/เดือน · ${formatUsd(worker.price_usd_month)}`;
}

/** 7960 MB → "8 GB"; under a gigabyte keeps one decimal ("0.5 GB"). */
export function memoryLabel(memoryMb: number): string {
  const gb = memoryMb / 1024;
  return gb < 1 ? `${Math.round(gb * 10) / 10} GB` : `${Math.round(gb)} GB`;
}

/** "4 vCPU · 8 GB · 160 GB"; a spec the machine has not reported is left out. */
export function specsLine(worker: Pick<Worker, "vcpus" | "memory_mb" | "disk_gb">): string {
  const parts: string[] = [];
  if (worker.vcpus > 0) parts.push(`${worker.vcpus} vCPU`);
  if (worker.memory_mb > 0) parts.push(memoryLabel(worker.memory_mb));
  if (worker.disk_gb > 0) parts.push(`${worker.disk_gb} GB`);
  return parts.join(" · ");
}

/** "provider · region", whichever is known. */
export function placeLine(worker: Pick<Worker, "provider" | "region">): string {
  return [worker.provider, worker.region].filter(Boolean).join(" · ");
}

/** How long the current round has run: "8 นาที", "1 ชม. 5 นาที". */
export function elapsedLabel(startedAt: string | null | undefined, now: Date): string {
  const start = parseStamp(startedAt);
  if (!start) return "";
  const minutes = Math.max(0, Math.floor((now.getTime() - start.getTime()) / 60_000));
  if (minutes < 1) return "ไม่ถึงนาที";
  if (minutes < 60) return `${minutes} นาที`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} ชม. ${rest} นาที` : `${hours} ชม.`;
}

/** The Bangkok calendar day the server stamps on `today.date`. */
export function bangkokDay(now: Date): string {
  return new Date(now.getTime() + 7 * 60 * 60_000).toISOString().slice(0, 10);
}

export type TodayCounts = { rounds: number; delivered: number; needs_you: number; failed: number };

/** Today's counters; yesterday's are zeros, since the server only resets them on the next round. */
export function todayCounts(today: WorkerToday | null | undefined, now: Date): TodayCounts {
  const current = today && today.date === bangkokDay(now) ? today : {};
  return {
    rounds: current.rounds || 0,
    delivered: current.delivered || 0,
    needs_you: current.needs_you || 0,
    failed: current.failed || 0,
  };
}

export function categoriesOf(worker: Pick<Worker, "categories">): string[] {
  return Array.isArray(worker.categories) ? worker.categories.filter((name) => typeof name === "string" && name.trim()) : [];
}

/** The key Flows derives from a category name (pb_hooks/flows-lib.js categoryKey). */
export function categoryKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, "-");
}

type CategoryRow = { id: string; key: string };

/**
 * Queued cards per category key, in the viewer's own Flows. A key the viewer has
 * no category for is missing, so the card can say so instead of showing 0.
 */
export function queuedByCategory(categories: readonly CategoryRow[], cards: readonly { category: string }[]): Map<string, number> {
  const keyOf = new Map(categories.map((category) => [category.id, category.key]));
  const counts = new Map<string, number>(categories.map((category) => [category.key, 0]));
  for (const card of cards) {
    const key = keyOf.get(card.category);
    if (key !== undefined) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

export type WorkersSummary = { online: number; total: number; working: number; thb: number; usd: number };

export function summarize(workers: readonly Worker[], now: Date): WorkersSummary {
  let online = 0;
  let working = 0;
  let thb = 0;
  let usd = 0;
  for (const worker of workers) {
    const status = workerStatus(worker, now);
    if (status !== "offline") online += 1;
    if (status === "working") working += 1;
    thb += thbPerMonth(worker) ?? 0;
    usd += worker.price_usd_month || 0;
  }
  return { online, total: workers.length, working, thb, usd };
}

/**
 * The list the editor saves: the ticked names in the order shown, then the
 * typed ones, without blanks or a second copy of the same key.
 */
export function mergeCategories(checked: readonly string[], added: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const name of [...checked, ...added.split(/[,\n]/)]) {
    const clean = name.trim();
    if (!clean || seen.has(categoryKey(clean))) continue;
    seen.add(categoryKey(clean));
    out.push(clean);
  }
  return out;
}
